import React from 'react'

import { Skeleton, Typography } from '@mui/material'
import CustomerContactsPhoneUsage from 'features/customers/components/usage/CustomerContactsPhoneUsage'
import CustomerContactsQuotaLimit from 'features/customers/components/usage/CustomerContactsQuotaLimit'
import CustomerEnrichApiQuotaUsage from 'features/customers/components/usage/CustomerEnrichApiQuotaUsage'
import CustomerLeadsUsage from 'features/customers/components/usage/CustomerLeadsUsage'
import CustomerRevealApiCompaniesUsage from 'features/customers/components/usage/CustomerRevealApiCompaniesUsage'
import CustomerRevealApiQuotaUsage from 'features/customers/components/usage/CustomerRevealApiQuotaUsage'
import NotEnabled from 'features/customers/components/usage/NotEnabled'
import UsageUnavailable from 'features/customers/components/usage/UsageUnavailable'
import {
  ENRICH_METERS,
  enrichMeterLabel,
  REVEAL_METERS,
  type RevealMeterId,
  revealMeterLabel,
} from 'features/customers/constants/apiMeters'
import { FEATURES } from 'features/customers/constants/features'
import { useCustomerId } from 'features/customers/hooks/useCustomerId'
import { useCustomerRevealApiQuota } from 'features/customers/hooks/useCustomerRevealApiQuota'
import { Link } from 'react-router-dom'
import { UrlProvider } from 'urls'

import { EFeatureNames, hasRevealConfig } from '@albacross/types'

import { LabelWithTooltip } from 'components/_base'
import { DescriptionListTable } from 'components/table'
import type { ColumnConfig } from 'components/table/types'

export type RevealApiState = 'pending' | 'error' | 'enabled' | 'disabled'

export const revealApiState = ({
  data,
  isPending,
  isError,
}: {
  data?: unknown
  isPending: boolean
  isError: boolean
}): RevealApiState => {
  if (isPending) return 'pending'
  if (isError) return 'error'

  return hasRevealConfig(data) ? 'enabled' : 'disabled'
}

const limitLabel = (label: string, name: EFeatureNames) => (
  <LabelWithTooltip label={label} tooltip={FEATURES[name].description} />
)

const RevealApiSummary = ({ state }: { state: Exclude<RevealApiState, 'enabled'> }) => {
  if (state === 'pending') return <Skeleton variant="text" width={85} />
  if (state === 'error') return <UsageUnavailable />

  return <NotEnabled />
}

const REVEAL_ROWS: ColumnConfig[] = (Object.keys(REVEAL_METERS) as RevealMeterId[]).map(id => ({
  title: <LabelWithTooltip label={revealMeterLabel(id)} tooltip={REVEAL_METERS[id].description} />,
  render: () =>
    id === 'uniqueIdentifiedCompanies' ? (
      <CustomerRevealApiCompaniesUsage />
    ) : (
      <CustomerRevealApiQuotaUsage type={id} />
    ),
}))

export const usageRows = (reveal: RevealApiState): ColumnConfig[] => [
  { title: limitLabel('Intent Companies', EFeatureNames.LeadsLimit), render: () => <CustomerLeadsUsage /> },
  {
    title: limitLabel('Email contacts', EFeatureNames.ContactsUsageLimit),
    render: () => <CustomerContactsQuotaLimit />,
  },
  {
    title: limitLabel('Phone contacts', EFeatureNames.ContactsPhoneNumbersLimit),
    render: () => <CustomerContactsPhoneUsage />,
  },
  ...(reveal === 'enabled'
    ? REVEAL_ROWS
    : [
        {
          title: (
            <LabelWithTooltip
              label="Reveal API"
              tooltip="Requests, identified requests, unique IPs and unique companies, metered per month once the customer has a Reveal API key."
            />
          ),
          render: () => <RevealApiSummary state={reveal} />,
        },
      ]),
  {
    title: <LabelWithTooltip label={enrichMeterLabel('requests')} tooltip={ENRICH_METERS.requests.description} />,
    render: () => <CustomerEnrichApiQuotaUsage />,
  },
]

const CustomerUsageOverview = () => {
  const customerId = useCustomerId()
  const reveal = useCustomerRevealApiQuota()

  return (
    <>
      <DescriptionListTable config={usageRows(revealApiState(reveal))} titleCellStyle={{ width: '45%' }} />

      <Typography color="text.secondary" component="p" sx={{ px: 2, py: 1.5 }} variant="caption">
        Read-only here. Limits are changed on the{' '}
        <Link to={UrlProvider.getUrl('customer', { id: customerId, tab: 'features' })}>Features tab</Link>, API quotas
        on the <Link to={UrlProvider.getUrl('customer', { id: customerId, tab: 'api' })}>API tab</Link>.
      </Typography>
    </>
  )
}

export default CustomerUsageOverview
